import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Globe, Handshake, Inbox } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, Panel, Pill, Empty, inputCls } from "@/components/kit";
import { api, errMsg, gbp, fmtDay, initials } from "@/lib/api";
import { FormDialog, SectionHead, Toggle } from "./common";
import { UpgradeGate } from "@/components/UpgradeGate";
import { ImageUpload } from "@/components/ImageUpload";

const BLANK = { name: "", tier: "supporter", logo_url: "", website: "", description: "", contact_name: "", contact_email: "", deal_value: "", renewal_date: "", is_public: true };
const TIER_TONE = { gold: "amber", silver: "slate", bronze: "red", supporter: "cyan" };

const SponsorCard = ({ s, onEdit, onDelete }) => (
  <Panel className="flex flex-col gap-4" data-testid={`sponsor-card-${s.id}`}>
    <div className="flex items-start gap-4">
      {s.logo_url ? <img src={s.logo_url} alt={s.name} className="h-14 w-14 rounded bg-white object-contain p-1" /> : <span className="flex h-14 w-14 items-center justify-center rounded bg-white/5 font-display text-xl font-black text-white">{initials(s.name)}</span>}
      <div className="min-w-0 flex-1">
        <Pill tone={TIER_TONE[s.tier]}>{s.tier}</Pill>
        <p className="mt-1 truncate font-display text-xl font-bold uppercase text-white">{s.name}</p>
        {s.website && <a href={s.website} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-slate-400 hover:text-white"><Globe className="h-3 w-3" /> {s.website.replace(/^https?:\/\//, "")}</a>}
      </div>
    </div>
    <div className="flex items-end justify-between">
      <div>
        <p className="font-display text-3xl font-black text-[#CCFF00]">{gbp(s.deal_value)}</p>
        <p className="text-xs text-slate-500">{s.renewal_date ? `Renews ${fmtDay(s.renewal_date)}` : "No renewal date"}{!s.is_public && " · hidden"}</p>
      </div>
      <div className="flex gap-1">
        <Button size="icon" variant="ghost" onClick={() => onEdit(s)} data-testid={`edit-sponsor-${s.id}`}><Pencil className="h-4 w-4" /></Button>
        <Button size="icon" variant="ghost" className="text-rose-400" onClick={() => onDelete(s)} data-testid={`delete-sponsor-${s.id}`}><Trash2 className="h-4 w-4" /></Button>
      </div>
    </div>
  </Panel>
);

const SponsorForm = ({ f, setF, teamId }) => {
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <>
      <Field label="Sponsor name"><Input required value={f.name} onChange={set("name")} data-testid="sponsor-name-input" /></Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Tier"><select className={inputCls} value={f.tier} onChange={set("tier")} data-testid="sponsor-tier-select"><option value="gold">Gold</option><option value="silver">Silver</option><option value="bronze">Bronze</option><option value="supporter">Supporter</option></select></Field>
        <Field label="Deal value (£)"><Input type="number" step="0.01" min="0" value={f.deal_value} onChange={set("deal_value")} data-testid="sponsor-value-input" /></Field>
      </div>
      <Field label="Logo"><ImageUpload value={f.logo_url} onChange={(v) => setF({ ...f, logo_url: v })} teamId={teamId} testid="sponsor-logo" contain /></Field>
      <Field label="Website"><Input value={f.website} onChange={set("website")} placeholder="https://…" data-testid="sponsor-website-input" /></Field>
      <Field label="Short description"><Input value={f.description} onChange={set("description")} data-testid="sponsor-description-input" /></Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Contact name"><Input value={f.contact_name} onChange={set("contact_name")} data-testid="sponsor-contact-name-input" /></Field>
        <Field label="Contact email"><Input type="email" value={f.contact_email} onChange={set("contact_email")} data-testid="sponsor-contact-email-input" /></Field>
      </div>
      <Field label="Renewal date"><Input type="date" value={f.renewal_date} onChange={set("renewal_date")} data-testid="sponsor-renewal-input" /></Field>
      <Toggle checked={f.is_public} onChange={(v) => setF({ ...f, is_public: v })} label="Show on public sponsor wall" testid="sponsor-public-toggle" />
    </>
  );
};

export default function Sponsors({ teamId, overview, setTab }) {
  if (!overview.features.sponsors) return <UpgradeGate feature="sponsors" setTab={setTab} />;
  return <SponsorsInner teamId={teamId} />;
}

function SponsorsInner({ teamId }) {
  const [sponsors, setSponsors] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(BLANK);

  const load = () => {
    api.get(`/teams/${teamId}/sponsors`).then((r) => setSponsors(r.data));
    api.get(`/teams/${teamId}/sponsor-enquiries`).then((r) => setEnquiries(r.data));
  };
  useEffect(() => {
    load();
  }, [teamId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    const body = { ...f, deal_value: Number(f.deal_value || 0) };
    try {
      if (editing) await api.put(`/teams/${teamId}/sponsors/${editing.id}`, body);
      else await api.post(`/teams/${teamId}/sponsors`, body);
      toast.success("Sponsor saved");
      setOpen(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (s) => {
    if (!window.confirm(`Remove ${s.name}?`)) return;
    await api.delete(`/teams/${teamId}/sponsors/${s.id}`);
    load();
  };

  return (
    <div className="space-y-6">
      <SectionHead title="Sponsors" text="Your sponsor wall, deal values and renewals. Businesses can enquire from your hub.">
        <Button className="rounded-full font-bold" onClick={() => { setEditing(null); setF(BLANK); setOpen(true); }} data-testid="add-sponsor-btn"><Plus className="mr-1 h-4 w-4" /> Add sponsor</Button>
      </SectionHead>
      {!sponsors.length && <Empty icon={Handshake} title="No sponsors yet" text="Add your first local business partner." />}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">{sponsors.map((s) => <SponsorCard key={s.id} s={s} onEdit={(x) => { setEditing(x); setF(x); setOpen(true); }} onDelete={del} />)}</div>
      <Panel>
        <p className="flex items-center gap-2 font-display text-xl font-bold uppercase text-white"><Inbox className="h-5 w-5 text-[#CCFF00]" /> Sponsorship enquiries</p>
        <div className="mt-4 divide-y divide-white/5">
          {enquiries.map((q) => (
            <div key={q.id} className="py-3" data-testid="sponsor-enquiry-row">
              <p className="font-semibold text-white">{q.company} <span className="font-normal text-slate-400">· {q.name} · <a className="text-[#CCFF00]" href={`mailto:${q.email}`}>{q.email}</a></span></p>
              {q.message && <p className="mt-1 text-sm text-slate-400">{q.message}</p>}
              <p className="mt-1 font-mono text-[11px] text-slate-500">{fmtDay(q.created_at)}</p>
            </div>
          ))}
          {!enquiries.length && <p className="text-sm text-slate-500">No enquiries yet. They arrive from the "Become a sponsor" button on your hub.</p>}
        </div>
      </Panel>
      <FormDialog open={open} onOpenChange={setOpen} title={editing ? "Edit sponsor" : "Add sponsor"} onSubmit={save} testid="sponsor-form"><SponsorForm f={f} setF={setF} teamId={teamId} /></FormDialog>
    </div>
  );
}
